import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  Image,
  Link,
  StyleSheet,
  Font,
} from "@react-pdf/renderer";
import profile from "../../data/profile.json";

/**
 * On-brand resume PDF — matches rosh.work visual language while staying
 * ATS-friendly (single-column reading order, standard section headings,
 * text-selectable, no tables/rotated text). Content 100% from profile.json.
 */

/* ---------- Fonts (Google Fonts static TTFs) ---------- */
Font.register({
  family: "Fraunces",
  fonts: [
    { src: "https://fonts.gstatic.com/s/fraunces/v38/6NUh8FyLNQOQZAnv9bYEvDiIdE9Ea92uemAk_WBq8U_9v0c2Wa0K7iN7hzFUPJH58nib1603gg7S2nfgRYIctxujDg.ttf", fontWeight: 400 },
    { src: "https://fonts.gstatic.com/s/fraunces/v38/6NUh8FyLNQOQZAnv9bYEvDiIdE9Ea92uemAk_WBq8U_9v0c2Wa0K7iN7hzFUPJH58nib1603gg7S2nfgRYIchRujDg.ttf", fontWeight: 500 },
    { src: "https://fonts.gstatic.com/s/fraunces/v38/6NUh8FyLNQOQZAnv9bYEvDiIdE9Ea92uemAk_WBq8U_9v0c2Wa0K7iN7hzFUPJH58nib1603gg7S2nfgRYIcaRyjDg.ttf", fontWeight: 600 },
    { src: "https://fonts.gstatic.com/s/fraunces/v38/6NVf8FyLNQOQZAnv9ZwNjucMHVn85Ni7emAe9lKqZTnbB-gzTK0K1ChJdt9vIVYX9G37lvd9sPEKsxx664UJf1h5Tf7W.ttf", fontWeight: 500, fontStyle: "italic" },
  ],
});
Font.register({
  family: "Space Grotesk",
  fonts: [
    { src: "https://fonts.gstatic.com/s/spacegrotesk/v22/V8mQoQDjQSkFtoMM3T6r8E7mF71Q-gOoraIAEj7oUUsj.ttf", fontWeight: 400 },
    { src: "https://fonts.gstatic.com/s/spacegrotesk/v22/V8mQoQDjQSkFtoMM3T6r8E7mF71Q-gOoraIAEj7aUUsj.ttf", fontWeight: 500 },
    { src: "https://fonts.gstatic.com/s/spacegrotesk/v22/V8mQoQDjQSkFtoMM3T6r8E7mF71Q-gOoraIAEj42Vksj.ttf", fontWeight: 600 },
    { src: "https://fonts.gstatic.com/s/spacegrotesk/v22/V8mQoQDjQSkFtoMM3T6r8E7mF71Q-gOoraIAEj4PVksj.ttf", fontWeight: 700 },
  ],
});
Font.registerHyphenationCallback((word) => [word]);

/* ---------- Palette ---------- */
const C = {
  bg:    "#EDE7D9",
  paper: "#F6F1E5",
  ink:   "#262016",
  body:  "#3D3428",
  muted: "#6B5F4F",
  faint: "#8A7E6B",
  rule:  "#D6CCB4",
  wood:  "#B5793F",
  mint:  "#23C795",
  blue:  "#19B5DE",
};

/* ---------- Styles ---------- */
const s = StyleSheet.create({
  page: {
    backgroundColor: C.bg,
    paddingTop: 36,
    paddingBottom: 34,
    paddingHorizontal: 40,
    color: C.body,
    fontFamily: "Space Grotesk",
    fontSize: 9.4,
    lineHeight: 1.45,
  },

  /* Header */
  name: {
    fontFamily: "Fraunces",
    fontWeight: 500,
    fontSize: 30,
    color: C.ink,
    lineHeight: 1.0,
    letterSpacing: -0.3,
  },
  taglineLead: {
    marginTop: 10,
    fontFamily: "Fraunces",
    fontWeight: 500,
    fontStyle: "italic",
    fontSize: 13.5,
    color: C.ink,
    lineHeight: 1.3,
  },
  taglineSub: {
    marginTop: 3,
    fontFamily: "Space Grotesk",
    fontWeight: 500,
    fontSize: 10.4,
    color: C.muted,
    lineHeight: 1.4,
  },
  taglineSig: {
    marginTop: 3,
    fontFamily: "Fraunces",
    fontStyle: "italic",
    fontWeight: 500,
    fontSize: 11.4,
    color: C.wood,
    lineHeight: 1.3,
  },
  contactRow: {
    marginTop: 12,
    fontSize: 9.2,
    color: C.muted,
    lineHeight: 1.55,
  },
  contactLink: { color: C.muted, textDecoration: "none" },
  contactSep: { color: C.wood },
  hairline: { height: 0.6, backgroundColor: C.rule, marginTop: 12 },

  /* Section partition — ornament */
  divider: {
    marginTop: 16,
    marginBottom: 4,
    flexDirection: "row",
    alignItems: "center",
  },
  dividerLine: { flex: 1, height: 0.6, backgroundColor: C.rule },
  dividerDot: {
    width: 4, height: 4, borderRadius: 2, backgroundColor: C.wood,
    marginHorizontal: 8,
  },

  /* Section heading */
  sectionWrap: { marginTop: 4 },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 6,
  },
  sectionBar: {
    width: 20,
    height: 2.5,
    backgroundColor: C.wood,
    marginRight: 8,
  },
  sectionHeading: {
    fontFamily: "Space Grotesk",
    fontWeight: 700,
    fontSize: 10.5,
    color: C.ink,
    textTransform: "uppercase",
  },

  /* Body text */
  paragraph: {
    color: C.body,
    fontSize: 9.6,
    marginBottom: 2,
  },
  trustedRow: {
    marginTop: 6,
    fontSize: 9.4,
    color: C.muted,
  },
  trustedLabel: {
    fontFamily: "Space Grotesk",
    fontWeight: 700,
    color: C.ink,
    textTransform: "uppercase",
    fontSize: 9,
  },

  /* Bullets */
  bulletRow: {
    flexDirection: "row",
    marginBottom: 2,
  },
  bulletMark: {
    width: 10,
    color: C.wood,
    fontFamily: "Space Grotesk",
    fontWeight: 700,
  },
  bulletText: {
    flex: 1,
    color: C.body,
    fontSize: 9.4,
    lineHeight: 1.5,
  },
  bulletBold: {
    fontFamily: "Space Grotesk",
    fontWeight: 700,
    color: C.ink,
  },

  /* Experience — proper vertical breathing between orgs */
  expBlock: { marginTop: 8, marginBottom: 2 },
  expDivider: {
    height: 0.6,
    backgroundColor: C.rule,
    marginTop: 6,
    marginBottom: 6,
  },
  orgName: {
    fontFamily: "Fraunces",
    fontWeight: 600,
    fontSize: 14.5,
    color: C.ink,
    lineHeight: 1.15,
    marginBottom: 3,
  },
  roleHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "baseline",
    marginTop: 8,
    marginBottom: 3,
  },
  roleTitle: {
    fontFamily: "Fraunces",
    fontWeight: 500,
    fontStyle: "italic",
    fontSize: 10.6,
    color: C.ink,
    flex: 1,
    paddingRight: 8,
  },
  roleDates: {
    fontFamily: "Space Grotesk",
    fontSize: 8.2,
    color: C.faint,
    textTransform: "uppercase",
    fontWeight: 600,
  },
  metricsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginBottom: 3,
    marginTop: 1,
  },
  metricChip: {
    fontFamily: "Space Grotesk",
    fontSize: 8.2,
    color: C.ink,
    paddingHorizontal: 6,
    paddingVertical: 1.5,
    marginRight: 3,
    marginBottom: 2,
    borderRadius: 8,
    backgroundColor: "#DCE9DE",
  },

  /* Case cards */
  caseGrid: { marginTop: 6 },
  caseSub: {
    fontFamily: "Space Grotesk",
    fontWeight: 700,
    fontSize: 11,
    textTransform: "uppercase",
    marginTop: 10,
    marginBottom: 6,
  },
  caseRow: { flexDirection: "row", marginBottom: 5 },
  caseMark: {
    width: 3,
    marginRight: 8,
    marginTop: 2,
    marginBottom: 2,
    borderRadius: 1.5,
  },
  caseBody: { flex: 1 },
  caseClient: {
    fontFamily: "Fraunces",
    fontWeight: 500,
    fontSize: 10.6,
    color: C.ink,
    lineHeight: 1.15,
    marginBottom: 1,
  },
  caseOutcome: {
    fontSize: 9.2,
    color: C.body,
    marginTop: 0,
    lineHeight: 1.5,
  },
  caseMetricInline: {
    fontFamily: "Space Grotesk",
    fontWeight: 700,
    color: C.ink,
  },

  /* Skills — pill/grid layout matching home page */
  chip: {
    fontFamily: "Space Grotesk",
    fontSize: 8.6,
    color: C.ink,
    paddingHorizontal: 7,
    paddingVertical: 2,
    marginRight: 4,
    marginBottom: 4,
    borderRadius: 8,
    backgroundColor: "#F6F1E5",
    borderWidth: 0.6,
    borderColor: C.rule,
  },
  chipAI: {
    fontFamily: "Space Grotesk",
    fontWeight: 500,
    fontSize: 8.6,
    color: C.ink,
    paddingHorizontal: 7,
    paddingVertical: 2,
    marginRight: 4,
    marginBottom: 4,
    borderRadius: 8,
    backgroundColor: "rgba(25,181,222,0.10)",
    borderWidth: 0.6,
    borderColor: "rgba(25,181,222,0.35)",
  },
  chipWrap: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginTop: 2,
  },
  toolGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginTop: 2,
    marginHorizontal: -3,
  },
  toolCard: {
    marginHorizontal: 3,
    marginBottom: 5,
    padding: 6,
    paddingHorizontal: 8,
    borderRadius: 5,
    borderWidth: 0.6,
    borderColor: C.rule,
    backgroundColor: "#F6F1E5",
  },
  toolName: {
    fontFamily: "Space Grotesk",
    fontWeight: 500,
    fontSize: 8.8,
    color: C.ink,
    lineHeight: 1.2,
  },
  toolLevel: {
    marginTop: 2,
    fontFamily: "Space Grotesk",
    fontWeight: 600,
    fontSize: 7,
    textTransform: "uppercase",
  },
  skillGroup: { marginTop: 12 },
  skillGroupLabel: {
    fontFamily: "Space Grotesk",
    fontWeight: 700,
    fontSize: 8.6,
    textTransform: "uppercase",
    marginBottom: 6,
  },

  /* Awards pill */
  awardsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginTop: 4,
    marginBottom: 4,
  },
  awardChip: {
    fontFamily: "Space Grotesk",
    fontWeight: 600,
    fontSize: 8.4,
    color: C.wood,
    paddingHorizontal: 7,
    paddingVertical: 2,
    marginRight: 4,
    marginBottom: 4,
    borderRadius: 8,
    backgroundColor: "rgba(181,121,63,0.14)",
    borderWidth: 0.6,
    borderColor: "rgba(181,121,63,0.35)",
  },

  /* Skills / Edu */
  skillLine: {
    marginBottom: 3,
    fontSize: 9.6,
    color: C.body,
    lineHeight: 1.5,
  },
  skillCat: {
    fontFamily: "Space Grotesk",
    fontWeight: 700,
    color: C.ink,
  },
  eduRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "baseline",
    marginBottom: 3,
    marginTop: 4,
  },
  eduDegree: {
    fontFamily: "Fraunces",
    fontWeight: 500,
    fontSize: 11.4,
    color: C.ink,
    flex: 1,
    paddingRight: 8,
  },
  eduYears: {
    fontFamily: "Space Grotesk",
    fontSize: 8.8,
    color: C.faint,
    textTransform: "uppercase",
    fontWeight: 600,
  },
  eduSub: {
    fontSize: 9.5,
    color: C.muted,
    marginBottom: 2,
  },
  eduAwards: {
    fontFamily: "Fraunces",
    fontStyle: "italic",
    fontWeight: 400,
    fontSize: 9.2,
    color: C.wood,
    marginBottom: 4,
  },

  /* Impact tiles (mirrors home page block) */
  impactGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginHorizontal: -3,
    marginTop: 10,
  },
  impactTile: {
    marginHorizontal: 3,
    marginBottom: 9,
    padding: 11,
    paddingTop: 13,
    borderRadius: 6,
    borderWidth: 0.6,
    borderColor: C.rule,
    backgroundColor: "#F1EADA",
    position: "relative",
  },
  impactBar: {
    position: "absolute",
    top: 0,
    left: 11,
    width: 26,
    height: 2.4,
    borderRadius: 1,
  },
  impactEyebrow: {
    fontFamily: "Space Grotesk",
    fontWeight: 700,
    fontSize: 7.6,
    textTransform: "uppercase",
    marginBottom: 5,
    marginTop: 3,
  },
  impactBig: {
    fontFamily: "Fraunces",
    fontWeight: 500,
    color: C.ink,
    lineHeight: 1.14,
    marginBottom: 4,
  },
  impactText: {
    fontFamily: "Space Grotesk",
    fontSize: 8.6,
    color: C.body,
    lineHeight: 1.55,
  },

  /* Exposure chapter blocks */
  chapterBlock: { marginTop: 8 },
  chapterTitle: {
    fontFamily: "Fraunces",
    fontWeight: 500,
    fontSize: 10.6,
    color: C.ink,
    lineHeight: 1.2,
    marginBottom: 2,
  },
  chapterBody: {
    fontSize: 9.6,
    color: C.body,
    lineHeight: 1.55,
  },

  /* Footer / QR — designed card */
  qrRow: {
    marginTop: 12,
    padding: 10,
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 8,
    backgroundColor: "#EFE5CE",
    borderLeft: 3,
    borderLeftColor: C.wood,
  },
  qrFrame: {
    width: 96,
    height: 96,
    padding: 4,
    marginRight: 14,
    backgroundColor: "#F6F1E5",
    borderRadius: 6,
    borderWidth: 0.6,
    borderColor: C.rule,
  },
  qrImg: { width: 88, height: 88 },
  qrEyebrow: {
    fontFamily: "Space Grotesk",
    fontWeight: 700,
    fontSize: 8.2,
    color: C.wood,
    textTransform: "uppercase",
    marginBottom: 3,
  },
  qrLead: {
    fontFamily: "Fraunces",
    fontStyle: "italic",
    fontWeight: 500,
    fontSize: 11.6,
    color: C.ink,
    marginBottom: 4,
    lineHeight: 1.2,
  },
  qrText: {
    fontSize: 8.6,
    color: C.body,
    lineHeight: 1.45,
    marginBottom: 3,
  },
  qrListRow: { flexDirection: "row", flexWrap: "wrap", marginBottom: 2 },
  qrChip: {
    fontFamily: "Space Grotesk",
    fontWeight: 600,
    fontSize: 8,
    color: C.ink,
    paddingHorizontal: 6,
    paddingVertical: 1.5,
    marginRight: 4,
    marginBottom: 3,
    borderRadius: 8,
    backgroundColor: "rgba(181,121,63,0.14)",
    borderWidth: 0.5,
    borderColor: "rgba(181,121,63,0.30)",
  },
  qrUrl: {
    marginTop: 5,
    fontFamily: "Space Grotesk",
    fontWeight: 700,
    fontSize: 9.6,
    color: C.wood,
  },
  qrUrlLabel: {
    fontFamily: "Space Grotesk",
    fontWeight: 600,
    fontSize: 8.2,
    color: C.muted,
    textTransform: "uppercase",
    marginRight: 4,
  },
});

/* ---------- helpers ---------- */
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
function fmtDate(iso) {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})/.exec(iso);
  if (!m) return iso;
  return `${MONTHS[parseInt(m[2], 10) - 1]} ${m[1]}`;
}
function clean(text) {
  if (text == null) return "";
  return String(text)
    .replace(/[→⇒➔➞]/g, "→")
    .replace(/[←⇐]/g, "←")
    .replace(/[—–]/g, "—")
    .replace(/[“”„]/g, '"')
    .replace(/[‘’‚]/g, "'")
    .replace(/[•●■]/g, "·")
    .replace(/…/g, "…")
    .replace(/\u00A0/g, " ");
}
function renderMixed(text, boldStyle) {
  const cleaned = clean(text);
  const parts = cleaned.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? (
      <Text key={i} style={boldStyle}>{p.slice(2, -2)}</Text>
    ) : (
      <Text key={i}>{p}</Text>
    )
  );
}
function taglines() {
  const p = profile.identity || {};
  const head = p.headline || "Driving business growth with the power of design thinking and AI.";
  const subline = p.subline || "15 years across business, corporate, design and AI. Your go-to human in the loop.";
  const parts = subline.split(".").map((s) => s.trim()).filter(Boolean);
  const sig = parts[parts.length - 1] || "Your go-to human in the loop.";
  const sub = parts.slice(0, -1).join(". ");
  return {
    head,
    sub: sub ? sub + "." : "",
    sig: sig.endsWith(".") ? sig : sig + ".",
  };
}

/* ---------- primitives ---------- */
const SectionHeading = ({ children, color = C.wood }) => (
  <View style={s.sectionHeader}>
    <View style={{ ...s.sectionBar, backgroundColor: color }} />
    <Text style={s.sectionHeading}>{children}</Text>
  </View>
);
const SectionDivider = () => (
  <View style={s.divider}>
    <View style={s.dividerLine} />
    <View style={s.dividerDot} />
    <View style={s.dividerLine} />
  </View>
);
const Bullet = ({ children }) => (
  <View style={s.bulletRow} wrap={false}>
    <Text style={s.bulletMark}>›</Text>
    <Text style={s.bulletText}>{children}</Text>
  </View>
);

/* ---------- Impact tiles ---------- */
const TILE_ACCENT = { wood: C.wood, mint: C.mint, blue: C.blue, ink: C.ink };
// PDF single page width ≈ A4 - 48*2 padding = ~499pt. 12-column at 3px gutter each side => col ≈ (499 - 12*6)/12 ~= 35pt
const COL_W = 36.5;
const GUTTER = 6;
const spanWidth = (span) => COL_W * span + GUTTER * (span - 1);

function ImpactTile({ tile }) {
  const accent = TILE_ACCENT[tile.accent] || C.wood;
  const spanMap = { small: 3, regular: 4, wide: 6 };
  const span = spanMap[tile.size] || 4;
  const w = spanWidth(span);
  const bigFont = tile.size === "small" ? 13 : 11;
  // Ensure equal-height rows: fixed minHeight per size, tuned to accommodate the tallest
  // content across all tiles in the row so shorter tiles stretch to match.
  const minHeightMap = { small: 72, regular: 96, wide: 88 };
  const minHeight = minHeightMap[tile.size] || 84;
  const tileBody = (
    <View style={{ ...s.impactTile, width: w, minHeight }} wrap={false}>
      <View style={{ ...s.impactBar, backgroundColor: accent }} />
      <Text style={{ ...s.impactEyebrow, color: accent }}>{clean(tile.eyebrow)}</Text>
      {tile.pair ? (
        <View style={{ flexDirection: "row" }}>
          {tile.pair.map((p, i) => (
            <View key={i} style={{ flex: 1, paddingRight: i === 0 ? 4 : 0 }}>
              <Text style={{ ...s.impactBig, fontSize: 12, color: accent }}>{clean(p.big)}</Text>
              <Text style={{ ...s.impactText, fontSize: 7.4 }}>{clean(p.label)}</Text>
            </View>
          ))}
        </View>
      ) : (
        <>
          <Text style={{ ...s.impactBig, fontSize: bigFont, color: tile.size === "small" ? accent : C.ink }}>
            {clean(tile.big)}
          </Text>
          <Text style={s.impactText}>{renderMixed(tile.text, { ...s.impactText, fontWeight: 700, color: C.ink })}</Text>
        </>
      )}
    </View>
  );
  if (tile.href) {
    return (
      <Link src={tile.href} style={{ textDecoration: "none" }}>
        {tileBody}
      </Link>
    );
  }
  return tileBody;
}
/* ---------- document ---------- */
export default function ResumeDocument() {
  const p = profile;
  const ident = p.identity || {};
  const override = p.resumeOverrides || {};
  const tl = taglines();
  const portfolioUrl = ident.portfolio || "https://www.rosh.work";
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=260x260&margin=2&format=png&color=262016&bgcolor=F6F1E5&data=${encodeURIComponent(portfolioUrl)}`;

  return (
    <Document
      title={`${ident.name} - Resume`}
      author={ident.name}
      keywords="AI, Design Thinking, Digital Transformation, UX, Learning & Development, Leadership, Enterprise Design, AI Readiness, Business Impact"
    >
      <Page size="A4" style={s.page}>
        {/* ========== HEADER ========== */}
        <View>
          <Text style={s.name}>{clean(ident.name)}</Text>
          <Text style={s.taglineLead}>{clean(tl.head)}</Text>
          {tl.sub ? <Text style={s.taglineSub}>{clean(tl.sub)}</Text> : null}
          <Text style={s.taglineSig}>{clean(tl.sig)}</Text>

          <View style={s.contactRow}>
            <Text>
              {ident.location ? clean(ident.location) : ""}
              {ident.email ? <>{"  "}<Text style={s.contactSep}>·</Text>{"  "}<Link src={`mailto:${ident.email}`} style={s.contactLink}>{clean(ident.email)}</Link></> : null}
              {ident.phone ? <>{"  "}<Text style={s.contactSep}>·</Text>{"  "}{clean(ident.phone)}</> : null}
            </Text>
            <Text style={{ marginTop: 3 }}>
              {ident.linkedin ? (
                <>
                  <Text style={{ color: C.wood, fontWeight: 700 }}>in</Text>
                  {"  "}
                  <Link src={ident.linkedin} style={s.contactLink}>
                    {clean(ident.linkedin.replace(/^https?:\/\/(www\.)?linkedin\.com\/in\//, "").replace(/\/$/, ""))}
                  </Link>
                </>
              ) : null}
              {ident.portfolio ? (
                <>
                  {"  "}<Text style={s.contactSep}>·</Text>{"  "}
                  <Text style={{ fontFamily: "Space Grotesk", fontWeight: 600, color: C.muted, fontSize: 8.6 }}>PORTFOLIO: </Text>
                  <Link src={ident.portfolio} style={{ ...s.contactLink, fontWeight: 700, color: C.ink }}>
                    {"www." + clean(ident.portfolio.replace(/^https?:\/\/(www\.)?/, ""))}
                  </Link>
                </>
              ) : null}
            </Text>
          </View>
          <View style={s.hairline} />
        </View>

        {/* ========== SUMMARY ========== */}
        <View style={s.sectionWrap}>
          <SectionHeading color={C.wood}>Summary</SectionHeading>
          <Text style={s.paragraph}>{clean(override.summary || p.summary)}</Text>
        </View>

        {/* ========== IMPACT (matches home page block) ========== */}
        {(p.impactTiles || []).length > 0 && (
          <>
            <SectionDivider />
            <View style={s.sectionWrap}>
              <SectionHeading color={C.blue}>Impact</SectionHeading>
              <View style={s.impactGrid}>
                {p.impactTiles.map((t) => (
                  <ImpactTile key={t.id} tile={t} />
                ))}
              </View>
            </View>
          </>
        )}

        {/* ========== EXPERIENCE (starts on a new page) ========== */}
        <View style={s.sectionWrap} break>
          <SectionHeading color={C.wood}>Experience</SectionHeading>
          {(p.experience || []).map((org, oi) => (
            <View
              key={oi}
              break={org.org === "Artashastra Studios"}
              style={oi === 0 ? { marginTop: 4 } : s.expBlock}
            >
              {oi > 0 && org.org !== "Artashastra Studios" && <View style={s.expDivider} />}
              {/* orgName + first role stay together across page breaks */}
              <View wrap={false}>
                <Text style={{ ...s.orgName, marginBottom: 6 }} minPresenceAhead={80}>
                  {clean(org.org)}
                </Text>
                {(() => {
                  const r = (org.roles || [])[0];
                  if (!r) return null;
                  return (
                    <>
                      <View style={s.roleHeader}>
                        <Text style={s.roleTitle}>{clean(r.title)}</Text>
                        <Text style={s.roleDates}>
                          {fmtDate(r.startDate)}  —  {r.endDate ? fmtDate(r.endDate) : "Present"}
                        </Text>
                      </View>
                    </>
                  );
                })()}
              </View>
              {/* First role bullets */}
              {(org.roles || [])[0] && (org.roles[0].bullets || []).map((b, bi) => (
                <Bullet key={`r0-${bi}`}>{renderMixed(b, s.bulletBold)}</Bullet>
              ))}
              {/* Subsequent roles */}
              {(org.roles || []).slice(1).map((r, ri) => (
                <View key={ri + 1} style={{ marginTop: 12 }}>
                  <View wrap={false}>
                    <View style={s.roleHeader}>
                      <Text style={s.roleTitle}>{clean(r.title)}</Text>
                      <Text style={s.roleDates}>
                        {fmtDate(r.startDate)}  —  {r.endDate ? fmtDate(r.endDate) : "Present"}
                      </Text>
                    </View>
                  </View>
                  {(r.bullets || []).map((b, bi) => (
                    <Bullet key={bi}>{renderMixed(b, s.bulletBold)}</Bullet>
                  ))}
                </View>
              ))}
            </View>
          ))}
        </View>

        {/* Selected Case Studies, Design Portfolio and Exposure & Social Impact are documented in full on the portfolio site — the QR block below leads recruiters straight to them. */}

        {/* ========== SKILLS ========== */}
        <View style={{ ...s.sectionWrap, marginTop: 14 }}>
          <SectionHeading color={C.wood}>Skills & Tools</SectionHeading>
          {(p.skills?.coreCompetencies || []).length > 0 && (
            <Text style={s.skillLine}>
              <Text style={s.skillCat}>Core competencies: </Text>
              {p.skills.coreCompetencies.map(clean).join(" · ")}
            </Text>
          )}
          <Text style={s.skillLine}>
            <Text style={s.skillCat}>Tools / Software / Platforms: </Text>
            Adobe Creative Suite · Adobe Photoshop · Canva · Google Flow (Nano Banana + Omni) · Adobe Premiere Pro · Vyond · ChatGPT · Claude · Adobe AI · Gemini Enterprise · HeyGen · Synthesia · Adobe After Effects · Adobe Illustrator · 3DS Max · Unreal Engine · WordPress · Figma · Emergent · Gemini · MidJourney · Runway · Seedance · ElevenLabs
          </Text>
        </View>

        {/* ========== EDUCATION ========== */}
        <View style={{ ...s.sectionWrap, marginTop: 8 }}>
          <SectionHeading color={C.wood}>Education</SectionHeading>
          {(p.education || []).map((e, i) => (
            <View key={i} wrap={false} style={{ marginTop: i === 0 ? 2 : 8 }}>
              <View style={s.eduRow}>
                <Text style={s.eduDegree}>{clean(e.degree)}</Text>
                <Text style={s.eduYears}>
                  {e.startYear ? `${e.startYear} — ` : ""}
                  {e.endYear || ""}
                </Text>
              </View>
              <Text style={s.eduSub}>
                {clean(e.institute)}
                {e.affiliation ? ` · ${clean(e.affiliation)}` : ""}
              </Text>
              {(e.awards || []).length > 0 && (
                <View style={s.awardsRow}>
                  {e.awards.map((a) => (
                    <Text key={a} style={s.awardChip}>{clean(a)}</Text>
                  ))}
                </View>
              )}
            </View>
          ))}
        </View>

        {/* ========== CERTIFICATIONS ========== */}
        {(p.certifications || []).length > 0 && (
          <View style={{ ...s.sectionWrap, marginTop: 8 }}>
            <SectionHeading color={C.blue}>Certifications</SectionHeading>
            {p.certifications.map((c, i) => (
              <Bullet key={i}>
                <Text style={s.bulletBold}>{clean(c.title)}</Text>
                {" — "}
                {clean(c.issuer)}
                {c.year ? ` (${c.year})` : ""}
              </Bullet>
            ))}
          </View>
        )}

        {/* ========== QR / PORTFOLIO CALLOUT ========== */}
        <Link src={portfolioUrl} style={{ textDecoration: "none" }}>
          <View style={s.qrRow} wrap={false}>
            <View style={s.qrFrame}>
              <Image src={qrSrc} style={s.qrImg} />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={s.qrEyebrow}>Explore the full portfolio</Text>
              <Text style={s.qrLead}>See case studies, videos & chatbot in one place.</Text>
              <View style={s.qrListRow}>
                <Text style={s.qrChip}>Case studies</Text>
                <Text style={s.qrChip}>Design portfolio</Text>
                <Text style={s.qrChip}>Video resume</Text>
                <Text style={s.qrChip}>AI chatbot</Text>
              </View>
              <Text>
                <Text style={s.qrUrlLabel}>SCAN OR VISIT</Text>
                <Text style={s.qrUrl}>{"www." + clean(portfolioUrl.replace(/^https?:\/\/(www\.)?/, ""))}</Text>
              </Text>
            </View>
          </View>
        </Link>

      </Page>
    </Document>
  );
}
